import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import type {
  UserMenuResponse,
  MainOption,
  SubOption,
  Section,
  SubSection,
  MenuPermissionActions
} from './menu.service';
import { environment } from '../../environments/environment';

/** Control manual. Poner en false y borrar withFirmaDigitalSection cuando el API ya envíe Firma digital. */
const SHOW_FIRMA_DIGITAL_MENU = true;

// Interfaces para la respuesta de la API (con 'label')
interface ApiMenuResponse {
  message: string;
  status: number;
  data: ApiUserMenuData;
}

interface ApiUserMenuData {
  defaultMainRoute: string;
  defaultSubRoute: string;
  defaultSectionRoute: string;
  defaultMenu?: ApiMainOption | null;
  mainOptions: ApiMainOption[];
}

interface ApiMainOption {
  label: string;
  route: string;
  icon: string;
  type?: string | null;
  subOptions: ApiSubOption[];
}

interface ApiSubOption {
  label: string;
  route: string;
  icon: string;
  showLevel?: boolean;
  permissionKey?: string | null;
  actions?: MenuPermissionActions | null;
  sections: ApiSection[];
}

interface ApiSection {
  label: string;
  route: string;
  icon: string | null;
  permissionKey?: string | null;
  actions?: MenuPermissionActions | null;
  subSections?: ApiSubSection[] | null;
}

interface ApiSubSection {
  label: string;
  route: string;
  icon: string | null;
  permissionKey?: string | null;
  actions?: MenuPermissionActions | null;
  subSections?: ApiSubSection[] | null;
}

// Interfaces para menús móviles
interface ApiMobileMenuResponse {
  message: string;
  status: number;
  data: ApiMobileMenuData;
}

interface ApiMobileMenuData {
  mobileMenus: ApiMobileMenuItem[];
  sidebarMenus: ApiMobileMenuItem[];
}

interface ApiMobileMenuItem {
  label: string;
  route: string;
  icon: {
    uuid: string;
    name: string;
    contentType: string;
    order: number;
    size: string;
    url: string;
  } | null;
  type: string;
  permissionKey?: string | null;
  actions?: MenuPermissionActions | null;
}

export interface MobileMenuResponse {
  mobileMenus: MobileMenuItem[];
  sidebarMenus: MobileMenuItem[];
}

export interface MobileMenuItem {
  name: string;
  route: string;
  icon: { url: string } | null;
  type: string;
  permissionKey: string | null;
  actions: MenuPermissionActions;
}

@Injectable({providedIn: 'root'})
export class MenuApiService {
  private baseUrl = environment.apiBase;

  constructor(private http: HttpClient) {}

  getUserMenu(): Observable<UserMenuResponse> {
    return this.http.get<ApiMenuResponse>(`${this.baseUrl}/menu/user`).pipe(
      map(response => this.adaptApiResponse(response)
    )

    );
  }

  getMobileMenu(): Observable<MobileMenuResponse> {
    return this.http.get<ApiMobileMenuResponse>(`${this.baseUrl}/menu/user/mobile`).pipe(
      map(response => this.adaptMobileApiResponse(response))
    );
  }

  private adaptApiResponse(apiResponse: ApiMenuResponse): UserMenuResponse {
    return {
      defaultMainRoute: apiResponse.data.defaultMainRoute,
      defaultSubRoute: apiResponse.data.defaultSubRoute,
      defaultSectionRoute: this.mapSectionRoute(apiResponse.data.defaultSectionRoute),
      defaultMenu: apiResponse.data.defaultMenu
        ? this.adaptMainOption(apiResponse.data.defaultMenu)
        : null,
      mainOptions: apiResponse.data.mainOptions.map(main => this.adaptMainOption(main))
    };
  }

  private adaptMobileApiResponse(apiResponse: ApiMobileMenuResponse): MobileMenuResponse {
    return {
      mobileMenus : apiResponse.data.mobileMenus.map(item => this.adaptMobileMenuItem(item)),
      sidebarMenus: apiResponse.data.sidebarMenus.map(item => this.adaptMobileMenuItem(item))
    };
  }

  private adaptMobileMenuItem(apiItem: ApiMobileMenuItem): MobileMenuItem {
    return {
      name: apiItem.label, // Convertir label a name
      route: this.mapMobileMenuRoute(apiItem.route),
      icon: apiItem.icon ? { url: apiItem.icon.url } : null,
      type: apiItem.type,
      permissionKey: apiItem.permissionKey ?? null,
      actions: this.adaptActions(apiItem.actions)
    };
  }

  private mapMobileMenuRoute(apiRoute: string): string {
    const normalized = (apiRoute || '').trim().replace(/^\/+/, '');
    if (!normalized) {
      return '/';
    }
    if (
      normalized === 'account'
      || normalized === 'crm/account'
      || normalized === 'crm/prospects/account'
    ) {
      return '/crm/prospects/account';
    }
    if (normalized.startsWith('crm/')) {
      return `/${normalized}`;
    }
    if (normalized.startsWith('sales-advisors-')) {
      return `/crm/sales-advisors/${normalized}`;
    }
    return `/${normalized}`;
  }

  private adaptMainOption(apiMain: ApiMainOption): MainOption {
    return {
      name: apiMain.label, // Convertir label a name
      route: apiMain.route,
      icon: apiMain.icon,
      type: apiMain.type ?? null,
      subOptions: (apiMain.subOptions ?? []).map(sub => this.adaptSubOption(sub))
    };
  }

  private adaptSubOption(apiSub: ApiSubOption): SubOption {
    return {
      name: apiSub.label, // Convertir label a name
      route: apiSub.route,
      icon: apiSub.icon,
      showLevel: apiSub.showLevel ?? true,
      permissionKey: apiSub.permissionKey ?? null,
      actions: this.adaptActions(apiSub.actions),
      sections: this.withFirmaDigitalSection(
        (apiSub.sections ?? []).map(section => this.adaptSection(section))
      )
    };
  }

  private withFirmaDigitalSection(sections: Section[]): Section[] {
    if (!SHOW_FIRMA_DIGITAL_MENU) {
      return sections;
    }

    const legalIndex = sections.findIndex(section => this.isLegalRepresentativesSection(section));
    if (legalIndex < 0) {
      return sections;
    }

    const next = sections.slice();
    next.splice(legalIndex + 1, 0, {
      name: 'Firma digital',
      route: 'digital-signature-settings',
      actions: {list: true, edit: true}
    });
    return next;
  }

  private isLegalRepresentativesSection(section: Section): boolean {
    const name = (section.name || '').trim().toLowerCase().replace(/\s+/g, ' ');
    return name === 'representantes legales' || section.route === 'legal-representatives';
  }

  private adaptSection(apiSection: ApiSection): Section {
    return {
      name: apiSection.label, // Convertir label a name
      route: this.mapSectionRoute(apiSection.route),
      icon: apiSection.icon || undefined,
      permissionKey: apiSection.permissionKey ?? null,
      actions: this.adaptActions(apiSection.actions),
      subSections: (apiSection.subSections ?? []).map(subSection => this.adaptSubSection(subSection))
    };
  }

  private adaptSubSection(apiSubSection: ApiSubSection): SubSection {
    const nestedSubSections = apiSubSection.subSections ?? [];
    return {
      name: apiSubSection.label, // Convertir label a name
      route: this.mapSubSectionRoute(apiSubSection.route),
      icon: apiSubSection.icon || undefined,
      permissionKey: apiSubSection.permissionKey ?? null,
      actions: this.adaptActions(apiSubSection.actions),
      subSections: nestedSubSections.length
        ? nestedSubSections.map(subSection => this.adaptSubSection(subSection))
        : undefined
    };
  }

  private adaptActions(actions: MenuPermissionActions | null | undefined): MenuPermissionActions {
    return {...(actions ?? {})};
  }

  // Mapear rutas de la API a rutas de Angular
  private mapSectionRoute(apiRoute: string): string {
    const routeMap: Record<string, string> = {
      // Panel de control
      'dashboard-prospects': 'prospects',
      'dashboard-consultants': 'consultants',
      'dashboard-campaigns': 'campaigns',
      'dashboard-sales': 'sales',
      'dashboard-records-panel': 'dashboard-records',
      'dashboard-inventory': 'inventory',
      'dashboard-deliveries': 'deliveries',
      'dashboard-clients': 'clients',
      'dashboard-owners': 'owners',
      'dashboard-virtual-agent': 'virtual-agent',

      // Prospectos
      'prospects-tracking': 'tracking',
      'prospects-appointments': 'appointments',
      'prospects-process': 'process',
      'prospects-surveys': 'surveys',
      'prospects-catalogs': 'catalogs',
      'prospects-reports': 'reports',

      // Conversaciones
      'conversations-list': 'conversations',
      'conversations-messages': 'messages',
      'conversations-catalogs': 'catalogs',
      'conversations-reports': 'reports',

      // Inventario
      'inventory-developments': 'developments',
      'inventory-models': 'models',
      'inventory-units': 'units',
      'inventory-catalogs': 'catalogs',
      'inventory-reports': 'reports',

      // Otros mapeos...
      'consultants-list': 'consultants',
      'consultants-real-estate': 'real-estate',
      'consultants-independent': 'independent-consultants',
      'consultants-surveys': 'surveys',
      'consultants-catalogs': 'catalogs',
      'consultants-reports': 'reports',

      // CRM - Expedientes
      'record-cancellation-requests': 'cancellation-requests',
      'crm-record-cancellation-requests': 'cancellation-requests'
    };

    return routeMap[apiRoute] || apiRoute;
  }

  private mapSubSectionRoute(apiRoute: string): string {
    const routeMap: Record<string, string> = {
      // Prospectos
      'general-data': 'general',

      // Conversaciones
      'conversations-whatsapp': 'whatsapp',
      'conversations-messenger': 'messenger',
      'conversations-instagram': 'instagram',

      // Inventario - Catálogos
      'inventory-amenities': 'amenities',
      'inventory-characteristics': 'characteristics',
      'inventory-interest-points': 'interest-points',

      // Sitio web - Clientes
      'site-clients-general': 'general',
      'site-clients-online-payment': 'online-payment',
      'site-clients-online-reserve': 'online-reserve'
    };

    return routeMap[apiRoute] || apiRoute;
  }
}
